import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { map, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';
import { IDevicePicture, IDevicePictureUpdate } from '@app/api/interfaces/device-picture.interface';
import { LoggerDeploymentPicturesMockService } from '@mock/data-services/logger-deployment-pictures-mock.service';
import { stripApiPrefix } from '@app/shared/utils/string';

function resolvePictureImageUrl(picture: IDevicePicture): IDevicePicture {
  return { ...picture, imageUrl: `${environment.apiBaseUrl}${stripApiPrefix(picture.imageUrl)}` };
}

@Injectable({
  providedIn: 'root',
})
export class LoggerDeploymentPicturesApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly MOCK: LoggerDeploymentPicturesMockService
  ) {}

  public getAll(
    projectId: string,
    deploymentId: string,
    pagination: Partial<IPaginationRequest> = {
      limit: 3,
      offset: 0,
    }
  ): Observable<IApiResponse<IDevicePicture[], IPaginationMeta>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getAll(projectId, deploymentId, pagination);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/pictures`;

    const params = new HttpParams({
      fromObject: {
        ...(pagination || {}),
      },
    });

    return this.httpClient
      .get<IApiResponse<IDevicePicture[], IPaginationMeta>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(map(response => ({ ...response, data: response.data.map(resolvePictureImageUrl) })));
  }

  public getOne(
    projectId: string,
    deploymentId: string,
    pictureId: string
  ): Observable<IApiResponse<IDevicePicture, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.getOne(projectId, deploymentId, pictureId);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/pictures/${pictureId}`;

    return this.httpClient
      .get<IApiResponse<IDevicePicture, object>>(url, {
        withCredentials: true,
      })
      .pipe(map(response => ({ ...response, data: resolvePictureImageUrl(response.data) })));
  }

  public update(
    projectId: string,
    deploymentId: string,
    pictureId: string,
    body: IDevicePictureUpdate
  ): Observable<IApiResponse<IDevicePicture, object>> {
    if (this.MOCK.isMockEnabled()) return this.MOCK.update(projectId, deploymentId, pictureId, body);

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/pictures/${pictureId}`;

    return this.httpClient
      .patch<IApiResponse<IDevicePicture, object>>(url, body, {
        withCredentials: true,
      })
      .pipe(map(response => ({ ...response, data: resolvePictureImageUrl(response.data) })));
  }
}
